import { useEffect, useRef } from 'react';
import { Button, Stack, Step } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import type { IPackagePreparerStepProps } from './PackagePreparerStep';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { FileAsset } from '~/components/molecules/FileAsset';
import { AssetUploader } from '~/components/organisms/fields/AssetUploader';
import {
  appResourcesQueryKeyFactory,
  useAppResourcesQuery,
} from '~/lib/api/modules/AppResources/hooks/useAppResourcesQuery';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { commonStyles } from '~/styles/commonStyles';

export type IPackagePreparerSplashScreenStepProps = IPackagePreparerStepProps;

const styles = stylex.create({
  splashScreen: {
    width: 57 * 3,
    height: 100 * 3,
    borderRadius: shapeTokens.corner$xs,
    backgroundColor: colorSchemeTokens.surface,
    borderWidth: 1,
    borderColor: colorSchemeTokens.outlineVariant,
    borderStyle: 'solid',
  },
  splashScreen$ios: {
    objectFit: 'cover',
  },
  splashScreen$android: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  splashScreenAppIcon$android: {
    width: '40%',
    aspectRatio: 1,
    borderRadius: shapeTokens.corner$full,
    backgroundSize: '140%',
    backgroundPosition: 'center',
  },
});

export const PackagePreparerSplashScreenStep: React.FC<
  IPackagePreparerSplashScreenStepProps
> = (props) => {
  const { appId, onSuccess, platform, ...other } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const appQuery = useAppQuery({ appId });
  const settingsQuery = useAppResourcesQuery({ appId });
  const assets = settingsQuery.data;
  const app = appQuery.data;
  const splashScreenBackgroundColor =
    app?.builds?.android?.splashScreenBackgroundColor;

  const isInitRef = useRef(false);
  const completed =
    !!assets && !!assets.platforms?.[platform]?.splashScreen?.downloadUrl;
  useEffect(() => {
    if (!assets || isInitRef.current) {
      return;
    }

    isInitRef.current = true;
    if (completed) {
      onSuccess?.({
        source: 'init',
      });
    }
  }, [assets, completed, onSuccess]);

  return (
    <Step
      {...other}
      label={t('PackagePreparerSplashScreenStep.name')}
      alwaysExpanded={!other.disabled}
    >
      {settingsQuery.isError ? (
        <ErrorMessage error={settingsQuery.error} />
      ) : settingsQuery.isLoading ? (
        t('common.loading')
      ) : assets ? (
        ({ active }) =>
          active ? (
            <Stack gap={4} align="start">
              <Trans
                parent="p"
                i18nKey="PackagePreparerSplashScreenStep.description"
              />
              <AssetUploader
                appId={appId}
                type={platform}
                format="splash"
                accept={{ 'image/png': ['.png'] }}
                downloadUrl={
                  assets.platforms?.[platform]?.splashScreen?.downloadUrl
                }
                onChange={(hasAsset) => {
                  if (hasAsset) {
                    void queryClient.invalidateQueries({
                      queryKey: appResourcesQueryKeyFactory({ appId }),
                    });
                  }
                }}
                renderFileAsset={(elementProps) => (
                  <FileAsset {...elementProps} hideMetadata />
                )}
              />
              <Button
                onClick={
                  onSuccess ? () => onSuccess({ source: 'user' }) : undefined
                }
                disabled={
                  !assets.platforms?.[platform]?.splashScreen?.downloadUrl
                }
              >
                {t('common.next')}
              </Button>
            </Stack>
          ) : assets.platforms?.[platform]?.splashScreen?.downloadUrl ? (
            platform === 'ios' ? (
              <img
                {...stylex.props(
                  styles.splashScreen,
                  styles.splashScreen$ios,
                  commonStyles.transparentCheckerboardBackground,
                )}
                src={assets.platforms[platform].splashScreen.downloadUrl}
                alt="App icon"
              />
            ) : (
              <div
                {...stylex.props(
                  styles.splashScreen,
                  styles[`splashScreen$${platform}`],
                  !!splashScreenBackgroundColor &&
                    commonStyles.backgroundColor(splashScreenBackgroundColor),
                )}
              >
                <div
                  {...stylex.props(
                    styles.splashScreenAppIcon$android,
                    commonStyles.backgroundImage(
                      assets.platforms[platform].splashScreen.downloadUrl,
                    ),
                  )}
                />
              </div>
            )
          ) : null
      ) : null}
    </Step>
  );
};

PackagePreparerSplashScreenStep.displayName = 'Step';
